/**
 * Holds a dropped card at its drop slot until the consumer's `items` change
 * (optimistic updates land a tick after the drop) or a timeout passes, so the
 * card never flashes back to its origin in between.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { KANBAN_SETTLE_TIMEOUT_MS } from './Kanban.motion';
import type { KanbanDropPreview } from './Kanban.config.types';

export interface KanbanSettlingDrop {
  itemId: string;
  preview: KanbanDropPreview;
}

export function useKanbanDropSettle(
  items: unknown,
  onLayoutWillChange?: () => void
) {
  const [settling, setSettling] = useState<KanbanSettlingDrop | null>(null);
  const settlingRef = useRef<KanbanSettlingDrop | null>(null);
  const onLayoutWillChangeRef = useRef(onLayoutWillChange);
  onLayoutWillChangeRef.current = onLayoutWillChange;

  const endSettle = useCallback(() => {
    if (!settlingRef.current) return;
    onLayoutWillChangeRef.current?.();
    settlingRef.current = null;
    setSettling(null);
  }, []);

  const startSettle = useCallback((next: KanbanSettlingDrop) => {
    settlingRef.current = next;
    setSettling(next);
  }, []);

  useEffect(() => {
    endSettle();
  }, [items, endSettle]);

  useEffect(() => {
    if (!settling) return;
    const timer = setTimeout(endSettle, KANBAN_SETTLE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [settling, endSettle]);

  return { settling, startSettle };
}
